import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { errMsg } from "../lib/api";
import PhotoInput from "../components/PhotoInput";
import { Download, Trash2, Gauge, Unlock, ScrollText } from "lucide-react";

const inputCls = "mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0E2240]";
const lbl = "text-[10px] font-semibold uppercase tracking-wider text-slate-500";
const dt = (s) => (s ? new Date(s).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short", timeZone: "Asia/Kolkata" }) : "—");

const downloadBlob = (data, filename) => {
  const url = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

function Card({ icon: Icon, title, subtitle, children }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <h3 className="flex items-center gap-2 font-heading text-sm font-bold text-[#0E2240]"><Icon size={15} /> {title}</h3>
      <p className="mt-0.5 text-[11px] text-slate-400">{subtitle}</p>
      {children}
    </div>
  );
}

function ClearData() {
  const [scope, setScope] = useState("");
  const [preview, setPreview] = useState(null);
  const [text, setText] = useState("");
  const pick = async (s) => {
    setScope(s); setPreview(null); setText("");
    const r = await api.get("/admin/clear-preview", { params: { scope: s } });
    setPreview(r.data);
  };
  const backup = async () => {
    toast.info("Generating full backup…");
    const r = await api.get("/admin/export-all", { responseType: "blob" });
    downloadBlob(r.data, "tdfms_full_backup.xlsx");
  };
  const doClear = async () => {
    try {
      const r = await api.post("/admin/clear", { scope, confirmation: text });
      toast.success(`Data cleared — ${Object.values(r.data.deleted).reduce((a, b) => a + b, 0)} records deleted`);
      setScope(""); setPreview(null); setText("");
      setTimeout(() => window.location.reload(), 1200);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const scopes = [["transactional", "Clear transactional data only", "Bookings, trips, gate passes, transfers, workshop, fuel, damage, expenses, notifications. Keeps fleet + org + settings."],
    ["vehicles", "Clear vehicles + transactional data", "Everything above plus all vehicles and EMI Master. Keeps org, users, rosters, models, settings."],
    ["full", "Full factory reset", "Everything except the Admin login itself."]];
  return (
    <Card icon={Trash2} title="Clear Data" subtitle="Testing/setup phase tool — irreversible, audit-logged, backup offered before deletion">
      <div className="mt-3 space-y-2">
        {scopes.map(([s, t, d]) => (
          <button key={s} data-testid={`clear-scope-${s}`} onClick={() => pick(s)}
            className={`w-full rounded-xl border p-3 text-left ${scope === s ? "border-[#BB162B] bg-red-50" : "border-slate-200"}`}>
            <div className="text-xs font-bold text-[#0E2240]">{t}</div>
            <div className="text-[10px] text-slate-400">{d}</div>
          </button>
        ))}
      </div>
      {preview && (
        <div data-testid="clear-preview" className="mt-3 rounded-xl bg-red-50 border border-red-200 p-3 text-xs">
          <div className="font-bold text-[#BB162B]">{preview.total} records will be permanently deleted:</div>
          <div className="mt-1 grid grid-cols-2 gap-x-3 text-[11px] text-slate-600">
            {Object.entries(preview.counts).filter(([, n]) => n > 0).map(([c, n]) => <div key={c}>{c}: <b>{n}</b></div>)}
          </div>
          <div className="mt-1.5 text-[10px] text-slate-500">Keeps: {preview.keeps}</div>
          <button data-testid="clear-backup-btn" onClick={backup} className="mt-2 flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-[11px] font-bold text-[#0E2240]"><Download size={12} /> Download full backup first</button>
          <label className={`${lbl} mt-3 block text-[#BB162B]`}>Type CLEAR ALL DATA to proceed</label>
          <input data-testid="clear-confirm-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="CLEAR ALL DATA" className={inputCls} />
          <button data-testid="clear-execute-btn" onClick={doClear} disabled={text !== "CLEAR ALL DATA"}
            className="mt-2 w-full rounded-xl bg-[#BB162B] py-3 text-sm font-bold text-white disabled:opacity-40">Permanently Delete</button>
        </div>
      )}
    </Card>
  );
}

function OdoCorrection() {
  const [vehicles, setVehicles] = useState([]);
  const [vid, setVid] = useState("");
  const [val, setVal] = useState("");
  const [reason, setReason] = useState("");
  const [photo, setPhoto] = useState(null);
  useEffect(() => { api.get("/vehicle-master").then((r) => setVehicles(r.data)).catch(() => {}); }, []);
  const v = vehicles.find((x) => x.id === vid);
  const submit = async (confirmReduction = false) => {
    try {
      await api.post(`/vehicles/${vid}/odometer-correction`, { new_value: parseInt(val), reason, photo, confirm_reduction: confirmReduction });
      toast.success("Odometer corrected — logged in vehicle history and km-reconciliation");
      setVal(""); setReason(""); setPhoto(null);
    } catch (e) {
      if (e.response?.status === 409 && !confirmReduction) {
        if (window.confirm(e.response.data.detail)) return submit(true);
        return;
      }
      toast.error(errMsg(e));
    }
  };
  return (
    <Card icon={Gauge} title="Odometer Correction" subtitle="New value + mandatory reason (+ optional photo). Reductions need extra confirmation. Never silently absorbs unexplained km.">
      <select data-testid="odo-vehicle-select" value={vid} onChange={(e) => setVid(e.target.value)} className={inputCls}>
        <option value="">Select vehicle…</option>
        {vehicles.filter((x) => !["sold", "retired"].includes(x.status)).map((x) => <option key={x.id} value={x.id}>{`${x.reg_no} — ${x.model} (${(x.current_odometer || 0).toLocaleString("en-IN")} km)`}</option>)}
      </select>
      {v && <p className="mt-1 text-[10px] text-slate-400">Current reading: <b>{(v.current_odometer || 0).toLocaleString("en-IN")} km</b></p>}
      <div className="mt-2 grid grid-cols-2 gap-2">
        <input data-testid="odo-new-value-input" type="number" value={val} onChange={(e) => setVal(e.target.value)} placeholder="Corrected km" className={inputCls} />
        <input data-testid="odo-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (mandatory)" className={inputCls} />
      </div>
      <div className="mt-2"><PhotoInput label="odometer photo (optional)" value={photo} onChange={setPhoto} testId="odo-photo-btn" /></div>
      <button data-testid="odo-submit-btn" onClick={() => submit(false)} disabled={!vid || !val || !reason.trim()}
        className="mt-3 w-full rounded-xl bg-[#0E2240] py-3 text-sm font-bold text-white disabled:opacity-40">Correct Odometer</button>
    </Card>
  );
}

function StuckRecords() {
  const [data, setData] = useState(null);
  const [closing, setClosing] = useState(null);
  const [reason, setReason] = useState("");
  const load = () => api.get("/admin/stuck").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const doClose = async () => {
    try {
      await api.post("/admin/force-close", { kind: closing.kind, id: closing.id, reason });
      toast.success("Force-closed — marked in the exception report");
      setClosing(null); setReason(""); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (!data) return null;
  const rows = [...data.trips.map((t) => ({ kind: "trip", id: t.id, label: `${t.reg_no} · ${t.kec_name} · ${t.status} · ${t.age_hours}h old` })),
    ...data.transfers.map((t) => ({ kind: "transfer", id: t.id, label: `${t.reg_no} · ${t.from_branch_name} → ${t.to_branch_name} · ${t.status}` }))];
  return (
    <Card icon={Unlock} title="Unlock Stuck Records" subtitle="Force-close a trip or transfer stuck mid-flow (e.g. app crash). Mandatory reason; appears in the daily exception report.">
      <div className="mt-2 space-y-2">
        {rows.length === 0 && <div className="rounded-xl border border-dashed border-slate-200 p-4 text-center text-xs text-slate-400">Nothing stuck 🎉</div>}
        {rows.map((r) => (
          <div key={r.id} className="rounded-xl border border-slate-200 p-3 text-xs">
            <div className="flex items-center justify-between gap-2">
              <span className="font-semibold text-[#0E2240]">{r.kind.toUpperCase()}: {r.label}</span>
              <button data-testid={`force-close-btn-${r.id}`} onClick={() => { setClosing(r); setReason(""); }}
                className="rounded-full border border-red-200 px-3 py-1 text-[10px] font-bold text-[#BB162B]">Force close</button>
            </div>
            {closing?.id === r.id && (
              <div className="mt-2 flex gap-2">
                <input data-testid="force-close-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (mandatory)…" autoFocus className={inputCls + " mt-0"} />
                <button data-testid="force-close-confirm-btn" onClick={doClose} disabled={!reason.trim()} className="rounded-xl bg-[#BB162B] px-3 text-[11px] font-bold text-white disabled:opacity-40">Close</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}

function Exporter() {
  const [entity, setEntity] = useState("vehicles");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const entities = ["vehicles", "trips", "bookings", "gate_passes", "transfers", "workshop", "fuel_logs",
    "damage_reports", "expenses", "emi_master", "emi_accruals", "kec_roster", "branches",
    "handovers", "disposal_records", "odometer_corrections", "audit_log", "users"];
  const run = async () => {
    toast.info("Preparing export…");
    const r = await api.get("/admin/export", { params: { entity, date_from: from || undefined, date_to: to || undefined }, responseType: "blob" });
    downloadBlob(r.data, `tdfms_${entity}.xlsx`);
  };
  return (
    <Card icon={Download} title="Full Data Export" subtitle="On-demand Excel export of any entity with date-range filters — same engine as the pre-clear backup">
      <div className="mt-2 grid grid-cols-3 gap-2">
        <select data-testid="export-entity-select" value={entity} onChange={(e) => setEntity(e.target.value)} className={inputCls}>
          {entities.map((x) => <option key={x} value={x}>{x.replace(/_/g, " ")}</option>)}
        </select>
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={inputCls} />
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={inputCls} />
      </div>
      <button data-testid="export-run-btn" onClick={run} className="mt-2 w-full rounded-xl bg-[#0E2240] py-2.5 text-sm font-bold text-white">Download Excel</button>
    </Card>
  );
}

function AuditViewer() {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ user_q: "", action: "", entity: "", date_from: "", date_to: "" });
  const load = () => api.get("/audit-log", { params: Object.fromEntries(Object.entries(f).filter(([, v]) => v)) }).then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  return (
    <Card icon={ScrollText} title="Audit Log Viewer" subtitle="Searchable trail of every action — user, action, entity, date range">
      <div className="mt-2 grid grid-cols-2 sm:grid-cols-5 gap-2">
        <input data-testid="audit-user-input" value={f.user_q} onChange={(e) => setF({ ...f, user_q: e.target.value })} placeholder="User…" className={inputCls} />
        <input data-testid="audit-action-input" value={f.action} onChange={(e) => setF({ ...f, action: e.target.value })} placeholder="Action…" className={inputCls} />
        <input value={f.entity} onChange={(e) => setF({ ...f, entity: e.target.value })} placeholder="Entity…" className={inputCls} />
        <input type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} className={inputCls} />
        <input type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} className={inputCls} />
      </div>
      <button data-testid="audit-search-btn" onClick={load} className="mt-2 rounded-full bg-[#0E2240] px-4 py-2 text-xs font-bold text-white">Search</button>
      <div className="mt-2 max-h-80 space-y-1.5 overflow-y-auto">
        {rows.map((a) => (
          <div key={a.id} className="rounded-lg bg-slate-50 p-2 text-[11px]">
            <b className="text-[#0E2240]">{a.user_name}</b> <span className="text-slate-400">({a.role})</span> · <span className="font-semibold">{a.action}</span> · {a.entity} {a.entity_id?.slice(0, 8)} · <span className="text-slate-400">{dt(a.ts)}</span>
          </div>
        ))}
        {rows.length === 0 && <div className="p-4 text-center text-xs text-slate-400">No entries match</div>}
      </div>
    </Card>
  );
}

export default function AdminUtilities() {
  return (
    <div className="mt-4 grid gap-4 lg:grid-cols-2">
      <ClearData />
      <OdoCorrection />
      <StuckRecords />
      <Exporter />
      <div className="lg:col-span-2"><AuditViewer /></div>
    </div>
  );
}
